import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Download, TrendingUp, MapPin, AlertTriangle, CalendarDays, ShoppingBag, Percent, RotateCcw, DollarSign, Truck, FileText } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";

// Preset date ranges — all in UTC ISO. `custom` unlocks two <input type=date>s.
const PRESETS = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "mtd", label: "Month to date" },
  { key: "qtd", label: "Quarter to date" },
  { key: "ytd", label: "Year to date" },
  { key: "1y", label: "Last 12 mo" },
  { key: "custom", label: "Custom" },
];

const computeRange = (preset, customFrom, customTo) => {
  const end = new Date();
  const start = new Date();
  switch (preset) {
    case "7d": start.setDate(end.getDate() - 7); break;
    case "30d": start.setDate(end.getDate() - 30); break;
    case "mtd": start.setDate(1); start.setHours(0, 0, 0, 0); break;
    case "qtd": {
      const q = Math.floor(end.getMonth() / 3) * 3;
      start.setMonth(q, 1); start.setHours(0, 0, 0, 0); break;
    }
    case "ytd": start.setMonth(0, 1); start.setHours(0, 0, 0, 0); break;
    case "1y": start.setFullYear(end.getFullYear() - 1); break;
    case "custom":
      return {
        from: customFrom ? new Date(customFrom).toISOString() : undefined,
        to: customTo ? new Date(customTo + "T23:59:59").toISOString() : undefined,
      };
    default:
      start.setDate(end.getDate() - 30);
  }
  return { from: start.toISOString(), to: end.toISOString() };
};

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString();

const Card = ({ title, icon: Icon, children, testid }) => (
  <div
    data-testid={testid}
    className="border p-6"
    style={{ borderColor: "var(--rs-border)", background: "var(--rs-surface)" }}
  >
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon size={14} strokeWidth={1.5} className="text-muted-bone" />}
      <div className="overline">{title}</div>
    </div>
    {children}
  </div>
);

const Kpi = ({ label, value, sub }) => (
  <div className="p-4 border" style={{ borderColor: "var(--rs-border)" }}>
    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-bone">{label}</div>
    <div className="font-serif-display text-3xl text-bone mt-1">{value}</div>
    {sub && <div className="text-xs text-muted-bone mt-1">{sub}</div>}
  </div>
);

const StatusPill = ({ status }) => {
  const colors = {
    ok: { color: "var(--rs-terracotta)", bg: "transparent" },
    watch: { color: "#f5d76e", bg: "rgba(245,215,110,0.10)" },
    exceeded: { color: "#ff8b7a", bg: "rgba(255,139,122,0.10)" },
    no_sales_tax: { color: "#7fa88c", bg: "transparent" },
  };
  const c = colors[status] || colors.ok;
  const label = status === "no_sales_tax" ? "no tax" : status;
  return (
    <span
      className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5"
      style={{ color: c.color, background: c.bg, border: `1px solid ${c.color}55` }}
    >
      {label}
    </span>
  );
};

const AdminReports = () => {
  const [preset, setPreset] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [opexPct, setOpexPct] = useState(15);   // default overhead assumption
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});

  const range = useMemo(() => computeRange(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { date_from: range.from, date_to: range.to };
    try {
      const [summary, pnl, vendors, states, nexus, annual, top, discounts, refunds, sla] = await Promise.all([
        api.get("/admin/reports/summary", { params }),
        api.get("/admin/reports/pnl", { params: { ...params, operating_expense_pct: opexPct } }),
        api.get("/admin/reports/sales-by-vendor", { params }),
        api.get("/admin/reports/sales-by-state", { params }),
        api.get("/admin/reports/nexus-watchlist", { params: { year } }),
        api.get("/admin/reports/annual-summary", { params: { year } }),
        api.get("/admin/reports/top-products", { params: { ...params, limit: 15 } }),
        api.get("/admin/reports/discount-roi", { params }),
        api.get("/admin/reports/refunds", { params }),
        api.get("/admin/reports/vendor-sla", { params }),
      ]);
      setData({
        summary: summary.data, pnl: pnl.data, vendors: vendors.data.rows,
        states: states.data.rows, nexus: nexus.data, annual: annual.data,
        top: top.data.rows, discounts: discounts.data.rows, refunds: refunds.data,
        sla: sla.data.rows,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reports failed to load");
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, opexPct, year]);

  useEffect(() => { load(); }, [load]);

  const downloadAddressCsv = () => _downloadFile("customer-addresses.csv",
    `ramshack-addresses-${(range.from || "").slice(0, 10)}_${(range.to || "").slice(0, 10)}.csv`,
    { date_from: range.from, date_to: range.to });

  const downloadStatementPdf = () => _downloadFile("statement.pdf",
    `ramshack-statement-${(range.from || "").slice(0, 10)}_${(range.to || "").slice(0, 10)}.pdf`,
    { date_from: range.from, date_to: range.to, year, operating_expense_pct: opexPct });

  function _downloadFile(endpoint, filename, params) {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ""))
    ).toString();
    fetch(`${BACKEND_URL}/api/admin/reports/${endpoint}?${qs}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("rs_token")}` },
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  }

  const monthName = (m) => new Date(2000, Number(m) - 1, 1).toLocaleString(undefined, { month: "short" });

  const { summary = {}, pnl = {}, vendors = [], states = [], nexus = {}, annual = {}, top = [], discounts = [], refunds = {}, sla = [] } = data;

  return (
    <div data-testid="admin-reports" className="px-6 md:px-10 py-10 max-w-[1400px]">
      <div className="flex items-center gap-3 mb-2">
        <BarChart3 size={16} strokeWidth={1.5} />
        <h1 className="font-serif-display text-3xl text-bone">Reports</h1>
      </div>
      <p className="text-muted-bone text-sm mb-8">
        Sales, tax nexus, P&amp;L, top movers, and every customer address —
        everything you need for accounting, filings, and running the house.
      </p>

      {/* Date range controls */}
      <div
        className="border p-4 mb-8 flex flex-wrap items-center gap-3"
        style={{ borderColor: "var(--rs-border)", background: "var(--rs-surface)" }}
      >
        <span className="text-[10px] uppercase tracking-[0.22em] text-muted-bone mr-2">Range —</span>
        {PRESETS.map((p) => (
          <button
            key={p.key}
            data-testid={`reports-preset-${p.key}`}
            onClick={() => setPreset(p.key)}
            className="text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 transition"
            style={{
              color: preset === p.key ? "var(--rs-bone)" : "var(--rs-terracotta)",
              background: preset === p.key ? "var(--rs-oxblood)" : "transparent",
              border: `1px solid ${preset === p.key ? "var(--rs-oxblood)" : "rgba(192,112,80,0.35)"}`,
            }}
          >
            {p.label}
          </button>
        ))}
        {preset === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <input data-testid="reports-custom-from" type="date" value={customFrom}
                   onChange={(e) => setCustomFrom(e.target.value)}
                   className="bg-transparent border px-3 py-1.5 text-xs text-bone"
                   style={{ borderColor: "var(--rs-border)" }} />
            <span className="text-muted-bone text-xs">→</span>
            <input data-testid="reports-custom-to" type="date" value={customTo}
                   onChange={(e) => setCustomTo(e.target.value)}
                   className="bg-transparent border px-3 py-1.5 text-xs text-bone"
                   style={{ borderColor: "var(--rs-border)" }} />
          </div>
        )}
        <div className="ml-auto flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-bone">Opex %</span>
            <input data-testid="reports-opex-input" type="number" min={0} max={100} step={1}
                   value={opexPct} onChange={(e) => setOpexPct(Number(e.target.value) || 0)}
                   className="w-16 bg-transparent border px-2 py-1 text-xs text-bone text-center"
                   style={{ borderColor: "var(--rs-border)" }} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-bone">Year</span>
            <input data-testid="reports-year-input" type="number" min={2020} max={2100}
                   value={year} onChange={(e) => setYear(Number(e.target.value))}
                   className="w-20 bg-transparent border px-2 py-1 text-xs text-bone text-center"
                   style={{ borderColor: "var(--rs-border)" }} />
          </div>
          <button data-testid="reports-export-pdf" onClick={downloadStatementPdf}
                  className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 text-bone hover:opacity-90"
                  style={{ background: "var(--rs-oxblood)" }}>
            <FileText size={12} strokeWidth={1.5} /> Statement PDF
          </button>
          <button data-testid="reports-export-csv" onClick={downloadAddressCsv}
                  className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 text-bone hover:opacity-90"
                  style={{ background: "var(--rs-oxblood)" }}>
            <Download size={12} strokeWidth={1.5} /> Address CSV
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-muted-bone italic text-sm mb-4">Loading reports…</div>
      )}

      {/* Top-line KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Kpi label="Gross revenue" value={money(summary.gross_revenue)} sub={`${num(summary.order_count)} orders`} />
        <Kpi label="AOV" value={money(summary.aov)} sub={`${num(summary.unit_count)} units`} />
        <Kpi label="Refunds" value={money(summary.refunded_amount)} sub={`${num(summary.refund_count)} orders`} />
        <Kpi label="Discounts redeemed" value={money(summary.discount_redeemed)} />
      </div>

      {/* P&L */}
      <Card title="Profit & Loss" icon={DollarSign} testid="report-pnl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Net revenue" value={money(pnl.net_revenue)} sub={`Gross ${money(pnl.gross_revenue)}`} />
          <Kpi label="COGS" value={money(pnl.cogs)} sub={`${num(pnl.units_sold)} units`} />
          <Kpi label="Gross profit" value={money(pnl.gross_profit)} sub={`${pnl.gross_margin_pct || 0}% margin`} />
          <Kpi label="Net profit" value={money(pnl.net_profit)} sub={`${pnl.net_margin_pct || 0}% net · after ${opexPct}% opex`} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-bone mt-4">
          Gross Revenue &minus; Discounts &minus; Refunds = Net Revenue &minus; COGS = Gross Profit &minus; Operating Exp. = Net Profit.
          Opex is modelled as a % of net revenue for quick what-ifs.
        </div>
      </Card>

      {/* Sales by vendor */}
      <div className="mt-8">
        <Card title="Sales by Vendor" icon={TrendingUp} testid="report-vendors">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-muted-bone border-b" style={{ borderColor: "var(--rs-border)" }}>
                <th className="py-2 pr-4">Vendor</th>
                <th className="py-2 pr-4 text-right">Orders</th>
                <th className="py-2 pr-4 text-right">Units</th>
                <th className="py-2 pr-4 text-right">Revenue</th>
                <th className="py-2 pr-4 text-right">COGS</th>
                <th className="py-2 pr-4 text-right">Profit</th>
                <th className="py-2 pr-4 text-right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {vendors.length === 0 && !loading && (
                <tr><td colSpan={7} className="py-8 text-center text-muted-bone italic">No orders in this window yet.</td></tr>
              )}
              {vendors.map((v, i) => (
                <tr key={i} className="text-bone border-b" style={{ borderColor: "var(--rs-border)" }}>
                  <td className="py-3 pr-4">{v.vendor}</td>
                  <td className="py-3 pr-4 text-right">{num(v.order_count)}</td>
                  <td className="py-3 pr-4 text-right">{num(v.units)}</td>
                  <td className="py-3 pr-4 text-right">{money(v.revenue)}</td>
                  <td className="py-3 pr-4 text-right">{money(v.cogs)}</td>
                  <td className="py-3 pr-4 text-right">{money(v.profit)}</td>
                  <td className="py-3 pr-4 text-right">{v.margin_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Vendor SLA */}
      <div className="mt-8">
        <Card title="Vendor SLA (Ship Time)" icon={Truck} testid="report-sla">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-bone mb-3">
            Time from paid → shipped, per vendor. Slowest at the top.
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-muted-bone border-b" style={{ borderColor: "var(--rs-border)" }}>
                <th className="py-2 pr-3">Vendor</th>
                <th className="py-2 pr-3 text-right">Shipped</th>
                <th className="py-2 pr-3 text-right">Pending</th>
                <th className="py-2 pr-3 text-right">Avg ship time</th>
                <th className="py-2 pr-3 text-right">Fastest / Slowest</th>
              </tr>
            </thead>
            <tbody>
              {sla.length === 0 && !loading && (
                <tr><td colSpan={5} className="py-8 text-center text-muted-bone italic">No shipped orders in this window yet.</td></tr>
              )}
              {sla.map((r, i) => (
                <tr key={i} className="text-bone border-b" style={{ borderColor: "var(--rs-border)" }}>
                  <td className="py-2 pr-3">{r.vendor}</td>
                  <td className="py-2 pr-3 text-right">{num(r.shipped_count)}</td>
                  <td className="py-2 pr-3 text-right">{num(r.pending_count)}</td>
                  <td className="py-2 pr-3 text-right">
                    {r.avg_hours == null ? "—" : `${r.avg_days}d (${r.avg_hours}h)`}
                  </td>
                  <td className="py-2 pr-3 text-right text-xs text-muted-bone">
                    {r.fastest_hours == null ? "—" : `${r.fastest_hours}h / ${r.slowest_hours}h`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Sales by state + Nexus */}
      <div className="grid lg:grid-cols-2 gap-8 mt-8">
        <Card title="Sales by State" icon={MapPin} testid="report-states">
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0" style={{ background: "var(--rs-surface)" }}>
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-muted-bone border-b" style={{ borderColor: "var(--rs-border)" }}>
                  <th className="py-2 pr-4">Country</th>
                  <th className="py-2 pr-4">State</th>
                  <th className="py-2 pr-4 text-right">Orders</th>
                  <th className="py-2 pr-4 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {states.length === 0 && !loading && (
                  <tr><td colSpan={4} className="py-8 text-center text-muted-bone italic">No shipping data yet.</td></tr>
                )}
                {states.map((r, i) => (
                  <tr key={i} className="text-bone border-b" style={{ borderColor: "var(--rs-border)" }}>
                    <td className="py-2 pr-4">{r.country}</td>
                    <td className="py-2 pr-4">{r.state}</td>
                    <td className="py-2 pr-4 text-right">{num(r.orders)}</td>
                    <td className="py-2 pr-4 text-right">{money(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Sales-Tax Nexus Watchlist" icon={AlertTriangle} testid="report-nexus">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-bone mb-3">
            {year} · per-state thresholds ·{" "}
            <span style={{ color: "#ff8b7a" }}>{nexus.exceeded_count || 0} exceeded</span> ·{" "}
            <span style={{ color: "#f5d76e" }}>{nexus.watch_count || 0} on watch</span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0" style={{ background: "var(--rs-surface)" }}>
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-muted-bone border-b" style={{ borderColor: "var(--rs-border)" }}>
                  <th className="py-2 pr-3">State</th>
                  <th className="py-2 pr-3 text-right">Revenue</th>
                  <th className="py-2 pr-3 text-right">Txns</th>
                  <th className="py-2 pr-3 text-right">Threshold</th>
                  <th className="py-2 pr-3 text-right">% used</th>
                  <th className="py-2 pr-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {(nexus.rows || []).length === 0 && !loading && (
                  <tr><td colSpan={6} className="py-8 text-center text-muted-bone italic">No US orders yet.</td></tr>
                )}
                {(nexus.rows || []).map((r, i) => {
                  const rev = r.revenue_threshold ? money(r.revenue_threshold) : "—";
                  const txn = r.transaction_threshold ? `${num(r.transaction_threshold)} txns` : null;
                  const pctBits = [];
                  if (r.revenue_pct_of_threshold != null) pctBits.push(`${r.revenue_pct_of_threshold}%$`);
                  if (r.txn_pct_of_threshold != null) pctBits.push(`${r.txn_pct_of_threshold}%#`);
                  return (
                    <tr key={i} className="text-bone border-b" style={{ borderColor: "var(--rs-border)" }}>
                      <td className="py-2 pr-3">{r.state}</td>
                      <td className="py-2 pr-3 text-right">{money(r.revenue)}</td>
                      <td className="py-2 pr-3 text-right">{num(r.orders)}</td>
                      <td className="py-2 pr-3 text-right text-xs text-muted-bone">
                        {rev}{txn ? ` ${r.rule_type} ${txn}` : ""}
                      </td>
                      <td className="py-2 pr-3 text-right text-xs">{pctBits.join(" · ") || "—"}</td>
                      <td className="py-2 pr-3 text-right"><StatusPill status={r.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {nexus.disclaimer && (
            <div className="text-[9px] italic text-muted-bone mt-3 leading-relaxed">
              {nexus.disclaimer}
            </div>
          )}
        </Card>
      </div>

      {/* Annual monthly summary */}
      <div className="mt-8">
        <Card title={`${annual.year || year} · Monthly Summary (1099-K Prep)`} icon={CalendarDays} testid="report-annual">
          <div className="grid grid-cols-4 md:grid-cols-12 gap-2">
            {(annual.months || []).map((m) => (
              <div key={m.month} className="p-3 border text-center" style={{ borderColor: "var(--rs-border)" }}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-bone">{monthName(m.month)}</div>
                <div className="font-serif-display text-lg text-bone mt-1">{money(m.revenue)}</div>
                <div className="text-[10px] text-muted-bone mt-1">{num(m.orders)} orders</div>
              </div>
            ))}
          </div>
          {annual.totals && (
            <div className="mt-4 pt-4 border-t flex justify-between items-baseline" style={{ borderColor: "var(--rs-border)" }}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-bone">Year total</div>
              <div className="font-serif-display text-2xl text-bone">
                {money(annual.totals.revenue)} · {num(annual.totals.orders)} orders · {num(annual.totals.units)} units
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Top products + Discounts + Refunds */}
      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        <Card title="Top Products" icon={ShoppingBag} testid="report-top">
          {top.length === 0 && !loading && (
            <div className="text-muted-bone italic text-sm py-4">No sales yet.</div>
          )}
          <ol className="space-y-3">
            {top.map((p, i) => (
              <li key={p.product_id || i} className="flex items-baseline justify-between gap-2 text-sm">
                <div className="text-bone truncate">
                  <span className="text-muted-bone mr-2">{i + 1}.</span>{p.name}
                </div>
                <div className="text-muted-bone whitespace-nowrap">
                  {num(p.units)} u · {money(p.revenue)}
                </div>
              </li>
            ))}
          </ol>
        </Card>

        <Card title="Discount Code ROI" icon={Percent} testid="report-discounts">
          {discounts.length === 0 && !loading && (
            <div className="text-muted-bone italic text-sm py-4">No codes redeemed.</div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-muted-bone border-b" style={{ borderColor: "var(--rs-border)" }}>
                <th className="py-2 pr-2">Code</th>
                <th className="py-2 pr-2 text-right">Orders</th>
                <th className="py-2 pr-2 text-right">Revenue</th>
                <th className="py-2 pr-2 text-right">Discounted</th>
              </tr>
            </thead>
            <tbody>
              {discounts.map((r, i) => (
                <tr key={i} className="text-bone border-b" style={{ borderColor: "var(--rs-border)" }}>
                  <td className="py-2 pr-2 font-mono text-xs">{r.code}</td>
                  <td className="py-2 pr-2 text-right">{num(r.orders)}</td>
                  <td className="py-2 pr-2 text-right">{money(r.revenue)}</td>
                  <td className="py-2 pr-2 text-right">{money(r.discount_given)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card title="Refunds" icon={RotateCcw} testid="report-refunds">
          <div className="space-y-4">
            <Kpi label="Refunded orders" value={num(refunds.refunded_orders)} sub={`of ${num(refunds.shipped_orders + refunds.refunded_orders || 0)} total`} />
            <Kpi label="Refunded $" value={money(refunds.refunded_amount)} sub={`${refunds.refund_rate_pct || 0}% refund rate`} />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminReports;
